import React, { useEffect, useMemo, useState } from 'react';
import HeaderLogged from './components/header_logged';
import api from './api/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxOpen, faTruckFast, faLock, faWarehouse, faArrowsRotate,
} from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer, ComposedChart, Area, Line, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import './AmazonInventoryReportPage.css';

// series colors in brand tones — validated for CVD (green/amber pair relies on
// the direct labels + legend that every chart here ships)
const C = { green: '#5B965D', amber: '#d9931a', blue: '#4a7fc4', greenDark: '#437045' };

const fmt = (n) => (n === null || n === undefined) ? '—' : Number(n).toLocaleString('en-US');
const num = (n) => Number(n) || 0;
const money = (n) => '$' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
// DATE columns arrive as 'YYYY-MM-DD' (or an ISO datetime from an older backend);
// never feed them to new Date() — the browser timezone would shift the day
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dateParts = (s) => String(s || '').slice(0, 10).split('-').map(Number);
const dayLabel = (s) => { const [, m, d] = dateParts(s); return m ? `${MONTHS_SHORT[m - 1]} ${d}` : '—'; };
const dayFull = (s) => { const [y, m, d] = dateParts(s); return m ? `${m}/${d}/${y}` : '—'; };

// sequential greens for the age buckets (one hue, light → dark)
const AGE_BUCKETS = [
  { key: 'age_0_90', label: '0–90 d', color: '#cfe4cf' },
  { key: 'age_91_180', label: '91–180 d', color: '#a9cdaa' },
  { key: 'age_181_270', label: '181–270 d', color: '#82b584' },
  { key: 'age_271_365', label: '271–365 d', color: '#5B965D' },
  { key: 'age_365_plus', label: '365+ d', color: '#3f6a41' },
];

const TOOLTIP_STYLE = {
  background: '#fff',
  border: 'none',
  borderRadius: 12,
  boxShadow: '0 8px 24px rgba(20,40,25,0.14)',
  fontSize: 12,
  padding: '10px 14px',
};

const AXIS_TICK = { fontSize: 11, fill: '#9ba3b0' };

// ---------------------------------------------------------------------------
// Donut: current FBA stock composition
// ---------------------------------------------------------------------------
const CompositionDonut = ({ parts, total }) => (
  <div className="air-donut">
    <div className="air-donut-chart">
      <ResponsiveContainer width={150} height={150}>
        <PieChart>
          <Pie
            data={parts} dataKey="value" nameKey="label"
            innerRadius="68%" outerRadius="100%"
            startAngle={90} endAngle={-270}
            paddingAngle={2} stroke="#fff" strokeWidth={2} isAnimationActive={false}
          >
            {parts.map(p => <Cell key={p.label} fill={p.color} />)}
          </Pie>
          <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, name) => [fmt(v), name]} />
        </PieChart>
      </ResponsiveContainer>
      <div className="air-donut-center">
        <div className="air-donut-total">{fmt(total)}</div>
        <div className="air-donut-caption">Total FBA</div>
      </div>
    </div>
    <div className="air-donut-legend">
      {parts.map(p => (
        <div key={p.label} className="air-donut-row">
          <span className="air-dot" style={{ background: p.color }} />
          <span className="air-donut-name">{p.label}</span>
          <b>{fmt(p.value)}</b>
          <i>{total ? Math.round((p.value / total) * 100) : 0}%</i>
        </div>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Daily stock history — area (Available) + lines (Inbound, AWD)
// ---------------------------------------------------------------------------
const HistoryChart = ({ history }) => {
  const rows = history.map(d => ({
    date: dayLabel(d.snapshot_date),
    Available: num(d.available),
    Inbound: num(d.inbound),
    AWD: num(d.awd_available),
  }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={rows} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="airGreenFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.green} stopOpacity={0.28} />
            <stop offset="100%" stopColor={C.green} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke="#eef1ee" />
        <XAxis dataKey="date" tick={AXIS_TICK} axisLine={false} tickLine={false} />
        <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={fmt} width={64} />
        <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => fmt(v)} />
        <Area type="monotone" dataKey="Available" stroke={C.green} strokeWidth={2.5}
              fill="url(#airGreenFill)" dot={{ r: 3 }} isAnimationActive={false} />
        <Line type="monotone" dataKey="Inbound" stroke={C.amber} strokeWidth={2}
              dot={{ r: 3 }} isAnimationActive={false} />
        <Line type="monotone" dataKey="AWD" stroke={C.blue} strokeWidth={2}
              dot={{ r: 3 }} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// ---------------------------------------------------------------------------
// Ledger monthly — soft rounded grouped bars
// ---------------------------------------------------------------------------
const LedgerChart = ({ ledger }) => {
  const rows = ledger.map(d => ({
    month: d.month.slice(2),
    Receipts: num(d.receipts),
    'Customer shipments': num(d.shipments),
  }));
  return (
    <ResponsiveContainer width="100%" height={270}>
      <BarChart data={rows} margin={{ top: 10, right: 16, left: 0, bottom: 0 }} barGap={3} barCategoryGap="28%">
        <CartesianGrid vertical={false} stroke="#eef1ee" />
        <XAxis dataKey="month" tick={AXIS_TICK} axisLine={false} tickLine={false} />
        <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={fmt} width={64} />
        <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => fmt(v)} cursor={{ fill: '#f2f6f2' }} />
        <Bar dataKey="Receipts" fill={C.green} radius={[7, 7, 7, 7]} maxBarSize={20} isAnimationActive={false} />
        <Bar dataKey="Customer shipments" fill={C.amber} radius={[7, 7, 7, 7]} maxBarSize={20} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const ChartLegend = ({ items }) => (
  <div className="air-legend-row">
    {items.map(([label, color]) => (
      <span key={label}><span className="air-dot" style={{ background: color }} /> {label}</span>
    ))}
  </div>
);

// Age buckets as five mini-tiles: with 90%+ of stock in the first bucket a
// proportional strip is unreadable — numbers per bucket say it better.
// Buckets past 180 days (where AIS fees start) go amber when non-empty.
const AgeTiles = ({ totals }) => {
  const total = AGE_BUCKETS.reduce((s, b) => s + num(totals[b.key]), 0) || 1;
  return (
    <div className="air-age-tiles">
      {AGE_BUCKETS.map((b, i) => (
        <div key={b.key} className={`air-age-tile${i >= 2 && num(totals[b.key]) > 0 ? ' warn' : ''}`}>
          <div className="air-age-head"><span className="air-dot" style={{ background: b.color }} />{b.label}</div>
          <div className="air-age-val">{fmt(totals[b.key])}</div>
          <div className="air-age-pct">{Math.round((num(totals[b.key]) / total) * 100)}% of stock</div>
        </div>
      ))}
    </div>
  );
};

// Monthly losses valued at current sales price
const LossesChart = ({ byMonth }) => {
  const rows = byMonth.map(m => ({
    month: m.month.slice(2),
    'Est. value': Number(m.est_value) || 0,
    units: num(m.lost_units) + num(m.damaged_units) + num(m.disposed_units),
  }));
  return (
    <ResponsiveContainer width="100%" height={190}>
      <BarChart data={rows} margin={{ top: 6, right: 12, left: 0, bottom: 0 }} barCategoryGap="35%">
        <CartesianGrid vertical={false} stroke="#eef1ee" />
        <XAxis dataKey="month" tick={AXIS_TICK} axisLine={false} tickLine={false} />
        <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={money} width={58} />
        <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#f2f6f2' }}
                 formatter={(v, name, p) => [`${money(v)} · ${fmt(p.payload.units)} units`, 'Lost + damaged + disposed']} />
        <Bar dataKey="Est. value" fill="#b94040" radius={[7, 7, 7, 7]} maxBarSize={22} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const DETAIL_COLS = [
  { key: 'available', label: 'Available' },
  { key: 'inbound', label: 'Inbound',
    tip: r => `working ${fmt(r.inbound_working)} + shipped ${fmt(r.inbound_shipped)} + receiving ${fmt(r.inbound_receiving)}` },
  { key: 'reserved_customer_orders', label: 'Res. orders' },
  { key: 'reserved_fc_transfer', label: 'FC transfer' },
  { key: 'reserved_fc_processing', label: 'FC processing' },
  { key: 'researching', label: 'Researching' },
  { key: 'unfulfillable', label: 'Unfulfillable' },
  { key: 'stranded', label: 'Stranded' },
  { key: 'awd_total', label: 'AWD total',
    tip: r => `AWD available ${fmt(r.awd_available)} + AWD inbound ${fmt(r.awd_inbound)}` },
  // same formula as YoY Forecast / Shipments Planning "FBA Stock"
  { key: 'fba_total', label: 'FBA total',
    tip: r => `available ${fmt(r.available)} + inbound ${fmt(r.inbound)} + FC transfer ${fmt(r.reserved_fc_transfer)} + FC processing ${fmt(r.reserved_fc_processing)}` },
];

const PILL = {
  Completed: 'ok', Pending: 'warn', Cancelled: 'muted', Inactive: 'muted', 'Search Suppressed': 'warn',
  Unsellable: 'muted', Sellable: 'warn', // sellable stock leaving deserves attention
};
const Pill = ({ children }) => <span className={`air-pill ${PILL[children] || 'muted'}`}>{children}</span>;

const AmazonInventoryReportPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'fba_total', dir: -1 });
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);

  const loadReport = () => api.get('/api/amazon-inventory-report')
    .then(({ data }) => setData(data))
    .catch((e) => setError(e.response?.data?.error || e.message));

  useEffect(() => { loadReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncError(null);
    const prevSync = data?.lastSync || null;
    try {
      // the server starts the sync in the background and responds instantly;
      // poll until lastSync moves (2-4 min), then swap in the fresh report
      await api.post('/api/sync-fba-stock');
      const deadline = Date.now() + 8 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 15000));
        const { data: fresh } = await api.get('/api/amazon-inventory-report');
        if (fresh.lastSync !== prevSync) {
          setData(fresh);
          setSyncing(false);
          return;
        }
      }
      setSyncError('Sync is taking longer than usual — refresh the page in a minute or two');
    } catch (e) {
      setSyncError(e.response?.data?.details || e.response?.data?.error || e.message);
    }
    setSyncing(false);
  };

  // ── Brand filter — Seedphony (SP) hidden by default ─────────────────
  const [hiddenBrands, setHiddenBrands] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem('air-hidden-brands')); return Array.isArray(v) ? v : ['SP']; }
    catch { return ['SP']; }
  });
  const toggleBrand = (b) => setHiddenBrands(h => {
    const next = h.includes(b) ? h.filter(x => x !== b) : [...h, b];
    localStorage.setItem('air-hidden-brands', JSON.stringify(next));
    return next;
  });
  // brand = prefix of the product name ("OR - ...", "SP - ..."); unknown → always shown
  const allBrands = useMemo(() => {
    const set = new Set();
    if (data) Object.values(data.names).forEach(n => { const b = String(n).split(' - ')[0].trim(); if (b) set.add(b); });
    return [...set].sort();
  }, [data]);
  const brandOf = (asin) => String((data && data.names[asin]) || '').split(' - ')[0].trim();
  const brandOk = (asin) => { const b = brandOf(asin); return !b || !hiddenBrands.includes(b); };
  const brandFiltered = hiddenBrands.length > 0;

  const { mappedRows, unmappedRows } = useMemo(() => {
    if (!data) return { mappedRows: [], unmappedRows: [] };
    const list = data.current.map(r => ({
      ...r,
      name: data.names[r.ASIN] || '',
      inbound: num(r.inbound_working) + num(r.inbound_shipped) + num(r.inbound_receiving),
      unfulfillable: num(r.unf_customer_damaged) + num(r.unf_warehouse_damaged) + num(r.unf_distributor_damaged)
        + num(r.unf_carrier_damaged) + num(r.unf_defective) + num(r.unf_expired),
      awd_total: num(r.awd_available) + num(r.awd_inbound),
      fba_total: num(r.available) + num(r.inbound_working) + num(r.inbound_shipped) + num(r.inbound_receiving)
        + num(r.reserved_fc_transfer) + num(r.reserved_fc_processing),
    }));
    const byBrand = list.filter(r => brandOk(r.ASIN));
    const q = search.trim().toLowerCase();
    const filtered = q
      ? byBrand.filter(r => r.ASIN.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)
          || (r.seller_skus || '').toLowerCase().includes(q))
      : byBrand;
    const bySort = (a, b) => (num(b[sort.key]) - num(a[sort.key])) * sort.dir * -1 || a.ASIN.localeCompare(b.ASIN);
    return {
      mappedRows: filtered.filter(r => r.mapped !== 0).sort(bySort),
      unmappedRows: filtered.filter(r => r.mapped === 0).sort(bySort),
    };
  }, [data, search, sort, hiddenBrands]); // eslint-disable-line react-hooks/exhaustive-deps

  const sumCols = (list) => {
    const t = {};
    DETAIL_COLS.forEach(c => { t[c.key] = list.reduce((s, r) => s + num(r[c.key]), 0); });
    return t;
  };
  const totals = useMemo(() => sumCols(mappedRows), [mappedRows]);
  const unmappedTotals = useMemo(() => sumCols(unmappedRows), [unmappedRows]);

  if (error) return (<><HeaderLogged /><div className="air-page"><div className="air-error">{error}</div></div></>);
  if (!data) return (<><HeaderLogged /><div className="air-page"><div className="air-loading">Loading report…</div></div></>);

  // tolerate an older backend that doesn't return these blocks yet
  const planning = data.planning || [];
  const losses = data.losses || { byMonth: [], byAsin: [] };
  const topReturns = data.topReturns || [];

  // hero numbers from the current snapshot, respecting the brand filter
  const heroRows = data.current.filter(r => r.mapped !== 0 && brandOk(r.ASIN));
  const hero = heroRows.reduce((a, r) => {
    a.available += num(r.available);
    a.inbWork += num(r.inbound_working); a.inbShip += num(r.inbound_shipped); a.inbRecv += num(r.inbound_receiving);
    a.resOrd += num(r.reserved_customer_orders); a.resTrans += num(r.reserved_fc_transfer); a.resProc += num(r.reserved_fc_processing);
    a.researching += num(r.researching);
    a.unf += num(r.unf_customer_damaged) + num(r.unf_warehouse_damaged) + num(r.unf_distributor_damaged)
      + num(r.unf_carrier_damaged) + num(r.unf_defective) + num(r.unf_expired);
    a.stranded += num(r.stranded);
    a.awdAv += num(r.awd_available); a.awdIn += num(r.awd_inbound);
    a.totalQ += num(r.total_quantity);
    return a;
  }, { available: 0, inbWork: 0, inbShip: 0, inbRecv: 0, resOrd: 0, resTrans: 0, resProc: 0,
       researching: 0, unf: 0, stranded: 0, awdAv: 0, awdIn: 0, totalQ: 0 });
  const heroInbound = hero.inbWork + hero.inbShip + hero.inbRecv;
  const heroReserved = hero.resOrd + hero.resTrans + hero.resProc;

  const visiblePending = data.removals.pending.filter(r => !r.ASIN || brandOk(r.ASIN));
  const visibleIssues = data.listingIssues.filter(r => !r.main_asin || brandOk(r.main_asin));
  const visibleLossAsins = losses.byAsin.filter(l => brandOk(l.ASIN));
  const visibleReturns = topReturns.filter(r => brandOk(r.ASIN));
  const removalInProcess = visiblePending.reduce((s, r) => s + num(r.in_process_qty), 0);
  const issueUnits = visibleIssues.reduce((s, r) => s + num(r.fba_units), 0);

  // Total FBA lives in the donut center — no fifth orphan tile
  const stockTiles = [
    { label: 'Available', value: hero.available, icon: faBoxOpen,
      sub: `${hero.totalQ ? Math.round((hero.available / hero.totalQ) * 100) : 0}% of FBA stock · ready to sell` },
    { label: 'Inbound', value: heroInbound, icon: faTruckFast,
      sub: `working ${fmt(hero.inbWork)} · shipped ${fmt(hero.inbShip)} · receiving ${fmt(hero.inbRecv)}` },
    { label: 'Reserved', value: heroReserved, icon: faLock,
      sub: `orders ${fmt(hero.resOrd)} · FC transfer ${fmt(hero.resTrans)} · processing ${fmt(hero.resProc)}` },
    { label: 'AWD', value: hero.awdAv, icon: faWarehouse,
      sub: `+ ${fmt(hero.awdIn)} inbound to AWD` },
  ];
  const attentionTiles = [
    { label: 'Researching', value: hero.researching },
    { label: 'Unfulfillable', value: hero.unf },
    { label: 'Stranded', value: hero.stranded },
    { label: 'Removals in process', value: removalInProcess },
    { label: 'Stock on problem listings', value: issueUnits },
  ];

  const sortBy = (key) => setSort(s => ({ key, dir: s.key === key ? -s.dir : -1 }));
  const numCell = (r, c) => (
    <td key={c.key}
        className={`air-num${['stranded', 'unfulfillable'].includes(c.key) && num(r[c.key]) > 0 ? ' neg' : ''}${['fba_total', 'awd_total'].includes(c.key) ? ' air-strong' : ''}`}
        title={c.tip ? c.tip(r) : undefined}>
      {num(r[c.key]) === 0 ? <span className="air-zero">0</span> : fmt(r[c.key])}
    </td>
  );

  return (
    <>
      <HeaderLogged />
      <div className="air-canvas">
        <div className="air-page">
          <div className="air-header">
            <div>
              <h2>Amazon Inventory Report</h2>
              <p className="air-subtitle">FBA stock · AWD · inventory ledger · removals · listing issues</p>
            </div>
            <div className="air-header-right">
              <div className="air-brand-filter">
                {allBrands.map(b => (
                  <button key={b}
                    className={`air-brand-btn${hiddenBrands.includes(b) ? ' off' : ''}`}
                    onClick={() => toggleBrand(b)}
                    title={hiddenBrands.includes(b) ? `Show ${b} products` : `Hide ${b} products`}>
                    {b}
                  </button>
                ))}
              </div>
              <span className="air-sync">Last sync: {data.lastSync ? new Date(data.lastSync).toLocaleString() : '—'}</span>
              <button className="air-sync-btn" onClick={handleSync} disabled={syncing}>
                <FontAwesomeIcon icon={faArrowsRotate} spin={syncing} />
                {syncing ? 'Syncing… (takes a minute or two)' : 'Sync now'}
              </button>
            </div>
          </div>
          {syncError && <div className="air-error" style={{ marginBottom: 16 }}>Sync failed: {syncError}</div>}

          {/* ── Hero: tiles + composition donut ─────────────────── */}
          <div className="air-hero">
            <div className="air-tiles">
              {stockTiles.map(t => (
                <div key={t.label} className="air-tile">
                  <span className="air-tile-icon"><FontAwesomeIcon icon={t.icon} /></span>
                  <div>
                    <div className="air-tile-value">{fmt(t.value)}</div>
                    <div className="air-tile-label">{t.label}</div>
                    {t.sub && <div className="air-tile-sub">{t.sub}</div>}
                  </div>
                </div>
              ))}
            </div>
            <div className="air-card air-hero-donut">
              <h3>Stock composition</h3>
              <CompositionDonut
                total={hero.totalQ}
                parts={[
                  { label: 'Available', value: hero.available, color: C.green },
                  { label: 'Inbound', value: heroInbound, color: C.amber },
                  { label: 'Reserved', value: heroReserved, color: C.blue },
                  { label: 'Other', value: Math.max(hero.totalQ - hero.available - heroInbound - heroReserved, 0), color: '#c5ddc6' },
                ].filter(p => p.value > 0)}
              />
            </div>
          </div>

          {/* ── Attention ─────────────────────────────────────────── */}
          <div className="air-group-label">Needs attention</div>
          <div className="air-tiles air-tiles--attention">
            {attentionTiles.map(t => (
              <div key={t.label} className={`air-tile${num(t.value) > 0 ? ' warn' : ' quiet'}`}>
                <div>
                  <div className="air-tile-value">{fmt(t.value)}</div>
                  <div className="air-tile-label">{t.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Inventory age & storage fees ──────────────────────── */}
          {planning.length > 0 && (() => {
            const pl = planning.filter(p => brandOk(p.ASIN));
            const pTotals = pl.reduce((a, p) => {
              AGE_BUCKETS.forEach(b => { a[b.key] = num(a[b.key]) + num(p[b.key]); });
              a.ais_qty += num(p.ais_qty); a.ais_est += Number(p.ais_est) || 0;
              a.storage += Number(p.storage_cost_next_month) || 0; a.excess += num(p.excess_qty);
              return a;
            }, { ais_qty: 0, ais_est: 0, storage: 0, excess: 0 });
            const aged = pl.filter(p => num(p.age_181_270) + num(p.age_271_365) + num(p.age_365_plus) > 0);
            return (
              <div className="air-card">
                <h3>Inventory age &amp; storage fees <span className="air-chip">planning report</span></h3>
                <AgeTiles totals={pTotals} />
                <div className="air-fee-chips">
                  <span className="air-fee-chip">Storage next month <b>{money(pTotals.storage)}</b></span>
                  <span className={`air-fee-chip${pTotals.ais_qty > 0 ? ' warn' : ''}`}>Aged surcharge (AIS) <b>{fmt(pTotals.ais_qty)} units → {money(pTotals.ais_est)}</b></span>
                  <span className={`air-fee-chip${pTotals.excess > 0 ? ' warn' : ''}`}>Excess stock <b>{fmt(pTotals.excess)} units</b></span>
                </div>
                {aged.length > 0 && (
                  <>
                    <h4 className="air-subhead">Products with stock older than 180 days</h4>
                    <div className="air-table-wrap">
                      <table className="air-table">
                        <thead><tr>
                          <th>Product</th>
                          <th className="air-num">181–270 d</th><th className="air-num">271–365 d</th><th className="air-num">365+ d</th>
                          <th className="air-num">AIS units</th><th className="air-num">AIS est.</th><th className="air-num">Days of supply</th>
                        </tr></thead>
                        <tbody>
                          {aged.map(p => (
                            <tr key={p.ASIN}>
                              <td>
                                <div className="air-product">{data.names[p.ASIN] || p.ASIN}</div>
                                <div className="air-asin">{p.ASIN}</div>
                              </td>
                              <td className="air-num">{fmt(p.age_181_270)}</td>
                              <td className={`air-num${num(p.age_271_365) > 0 ? ' neg' : ''}`}>{fmt(p.age_271_365)}</td>
                              <td className={`air-num${num(p.age_365_plus) > 0 ? ' neg' : ''}`}>{fmt(p.age_365_plus)}</td>
                              <td className="air-num">{fmt(p.ais_qty)}</td>
                              <td className={`air-num${Number(p.ais_est) > 0 ? ' neg' : ''}`}>{money(p.ais_est)}</td>
                              <td className="air-num">{fmt(p.days_of_supply)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                <p className="air-note">
                  Age buckets — how long units have been sitting at Amazon FCs. <b>AIS</b> (aged inventory surcharge, ex-LTSF) —
                  extra monthly fee Amazon charges for stock older than 180 days; the estimate is Amazon's own projection.
                  <b> Storage next month</b> — Amazon's estimated regular storage fee. <b>Excess</b> — units above Amazon's recommended level.
                </p>
              </div>
            );
          })()}

          {/* ── History ───────────────────────────────────────────── */}
          {data.history.length > 1 ? (
            <div className="air-card">
              <div className="air-card-head">
                <h3>Stock history <span className="air-chip">daily</span>{brandFiltered && <span className="air-chip muted">all brands</span>}</h3>
                <ChartLegend items={[['Available', C.green], ['Inbound', C.amber], ['AWD', C.blue]]} />
              </div>
              <HistoryChart history={data.history} />
              <p className="air-note">
                <b>Available</b> — units ready to sell · <b>Inbound</b> — on shipments created / on the way to Amazon FCs ·
                <b> AWD</b> — units in Amazon Warehousing &amp; Distribution. One point per day, synced at 1:00, 6:00 and 17:00 (Miami).
              </p>
            </div>
          ) : (
            <div className="air-hint">
              <b>Stock history (daily)</b> — starts from the first API sync
              {data.history[0] ? ` (${dayFull(data.history[0].snapshot_date)})` : ''}.
              One snapshot so far; the chart appears once there are two days of data.
            </div>
          )}

          {/* ── Ledger ────────────────────────────────────────────── */}
          <div className="air-card">
            <div className="air-card-head">
              <h3>Monthly flow <span className="air-chip">ledger, 12 mo</span>{brandFiltered && <span className="air-chip muted">all brands</span>}</h3>
              <ChartLegend items={[['Receipts', C.green], ['Customer shipments', C.amber]]} />
            </div>
            {data.ledger.length > 0 ? <LedgerChart ledger={data.ledger} /> : <div className="air-empty">No ledger data yet.</div>}
            <p className="air-note">
              <b>Receipts</b> — units checked in (received) at Amazon fulfillment centers that month ·
              <b> Customer shipments</b> — units shipped out to customers. Data from the Amazon inventory ledger.
            </p>
          </div>

          {data.ledger.length > 0 && (
            <div className="air-card">
              <h3>Monthly events <span className="air-chip">ledger</span>{brandFiltered && <span className="air-chip muted">all brands</span>}</h3>
              <div className="air-table-wrap">
                <table className="air-table">
                  <thead><tr>
                    <th>Month</th>
                    <th className="air-num">Receipts</th><th className="air-num">Shipments</th><th className="air-num">Returns</th>
                    <th className="air-num">Found</th><th className="air-num">Lost</th>
                    <th className="air-num">Damaged</th><th className="air-num">Disposed</th>
                    <th className="air-num">Sell-through</th>
                  </tr></thead>
                  <tbody>
                    {[...data.ledger].reverse().map(m => {
                      const avgStock = (num(m.sellable_start) + num(m.sellable_end)) / 2;
                      const st = avgStock > 0 ? num(m.shipments) / avgStock : null;
                      return (
                        <tr key={m.month}>
                          <td className="air-mono">{m.month}</td>
                          <td className="air-num">{fmt(m.receipts)}</td>
                          <td className="air-num">{fmt(m.shipments)}</td>
                          <td className="air-num">{fmt(m.returns_qty)}</td>
                          <td className="air-num">{fmt(m.found)}</td>
                          <td className={`air-num${num(m.lost) < 0 ? ' neg' : ''}`}>{fmt(m.lost)}</td>
                          <td className={`air-num${num(m.damaged) !== 0 ? ' neg' : ''}`}>{fmt(m.damaged)}</td>
                          <td className="air-num">{fmt(m.disposed)}</td>
                          <td className={`air-num air-strong${st !== null && st < 0.3 ? ' neg' : ''}`}>{st === null ? '—' : st.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="air-note">
                <b>Returns</b> — customer returns received back · <b>Found / Lost</b> — units the warehouse found or lost ·
                <b> Damaged</b> — damaged at the FC · <b>Disposed</b> — destroyed by Amazon. Negative numbers = stock leaving.
                <b> Sell-through</b> — shipments ÷ average sellable stock that month; below 0.30 hints at overstock.
              </p>
            </div>
          )}

          {/* ── Losses in money & top returns ─────────────────────── */}
          <div className="air-columns">
            <div className="air-card">
              <h3>Losses at Amazon <span className="air-chip">valued at sales price</span>{brandFiltered && <span className="air-chip muted">chart: all brands</span>}</h3>
              {losses.byMonth.length > 0 ? <LossesChart byMonth={losses.byMonth} /> : <div className="air-empty">No loss data yet.</div>}
              {visibleLossAsins.length > 0 && (
                <>
                  <h4 className="air-subhead">Top products by losses <span className="air-chip">12 mo</span></h4>
                  <div className="air-table-wrap">
                    <table className="air-table">
                      <thead><tr>
                        <th>Product</th>
                        <th className="air-num">Lost</th><th className="air-num">Found back</th>
                        <th className="air-num">Damaged</th><th className="air-num">Disposed</th><th className="air-num">Est. value</th>
                      </tr></thead>
                      <tbody>
                        {visibleLossAsins.map(l => (
                          <tr key={l.ASIN}>
                            <td>
                              <div className="air-product">{data.names[l.ASIN] || l.ASIN}</div>
                              <div className="air-asin">{l.ASIN}</div>
                            </td>
                            <td className="air-num neg">{fmt(l.lost_units)}</td>
                            <td className="air-num">{fmt(l.found_units)}</td>
                            <td className="air-num">{fmt(l.damaged_units)}</td>
                            <td className="air-num">{fmt(l.disposed_units)}</td>
                            <td className="air-num air-strong">{money(l.est_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <p className="air-note">
                Units Amazon lost, damaged or destroyed (inventory ledger, 12 mo), valued at the <b>current sales price</b> —
                the basis Amazon uses for reimbursements. <b>Found back</b> — previously lost units located again;
                lost minus found that stays unreimbursed is claim material.
              </p>
            </div>

            <div className="air-card">
              <h3>Top returns <span className="air-chip">12 mo</span></h3>
              <div className="air-table-wrap">
                <table className="air-table">
                  <thead><tr>
                    <th>Product</th>
                    <th className="air-num">Returned</th><th className="air-num">Shipped</th><th className="air-num">Return rate</th>
                  </tr></thead>
                  <tbody>
                    {visibleReturns.map(r => {
                      const rate = num(r.shipped_units) > 0 ? (num(r.returned_units) / num(r.shipped_units)) * 100 : null;
                      return (
                        <tr key={r.ASIN}>
                          <td>
                            <div className="air-product">{data.names[r.ASIN] || r.ASIN}</div>
                            <div className="air-asin">{r.ASIN}</div>
                          </td>
                          <td className="air-num">{fmt(r.returned_units)}</td>
                          <td className="air-num">{fmt(r.shipped_units)}</td>
                          <td className={`air-num air-strong${rate !== null && rate > 3 ? ' neg' : ''}`}>
                            {rate === null ? '—' : rate.toFixed(1) + '%'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="air-note">
                Customer returns from the inventory ledger, 12 months. <b>Return rate</b> = returned ÷ shipped;
                rates above ~3% are worth a look (listing accuracy, packaging, seed quality complaints).
              </p>
            </div>
          </div>

          {/* ── Per-ASIN table ────────────────────────────────────── */}
          <div className="air-card">
            <div className="air-section-head">
              <h3>
                Current stock by ASIN <span className="air-chip">{mappedRows.length} products</span>
                {unmappedRows.length > 0 && <span className="air-chip warn">{unmappedRows.length} not mapped</span>}
              </h3>
              <input
                className="air-search" placeholder="Search ASIN, SKU or name…"
                value={search} onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="air-table-wrap air-table-tall">
              <table className="air-table air-table--stock">
                <thead>
                  <tr>
                    <th className="sticky-col">Product</th>
                    {DETAIL_COLS.map(c => (
                      <th key={c.key} className="sortable air-num" onClick={() => sortBy(c.key)}>
                        {c.label}<span className="air-sort">{sort.key === c.key ? (sort.dir === -1 ? '↓' : '↑') : ''}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mappedRows.map(r => (
                    <tr key={r.ASIN}>
                      <td className="sticky-col">
                        <div className="air-product">{r.name || r.ASIN}</div>
                        <div className="air-asin">{r.ASIN}</div>
                      </td>
                      {DETAIL_COLS.map(c => numCell(r, c))}
                    </tr>
                  ))}
                  {unmappedRows.map(r => (
                    <tr key={r.ASIN} className="air-unmapped-row">
                      <td className="sticky-col">
                        <div className="air-product">
                          <span className="air-badge">Not mapped</span>
                        </div>
                        <div className="air-asin">{r.ASIN} · {r.seller_skus || '—'}</div>
                      </td>
                      {DETAIL_COLS.map(c => numCell(r, c))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="sticky-col">Total — mapped</td>
                    {DETAIL_COLS.map(c => <td key={c.key} className="air-num">{fmt(totals[c.key])}</td>)}
                  </tr>
                  {unmappedRows.length > 0 && (
                    <tr className="air-unmapped-row">
                      <td className="sticky-col">Not mapped</td>
                      {DETAIL_COLS.map(c => <td key={c.key} className="air-num">{fmt(unmappedTotals[c.key])}</td>)}
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
            <p className="air-note">
              <b>FBA total</b> = available + inbound + FC transfer + FC processing — the same formula as "FBA Stock"
              on YoY Forecast and Shipments Planning, so the numbers match. Res. orders (sold, awaiting shipment),
              researching, unfulfillable and stranded are shown separately and not included.
              <b> Note:</b> Amazon's API reports FC processing as one number that already INCLUDES "Reserved Staging" —
              in the downloadable FBA Inventory file this is split into two columns (Reserved FC Processing + Reserved
              Staging), so add both when comparing against that file.
              <b> AWD total</b> = AWD available + AWD inbound. Hover a total to see its breakdown.
              {unmappedRows.length > 0 && (
                <>
                  {' '}<b>Not mapped</b> — ASINs holding stock at Amazon that match no product in our catalog
                  (sets, packets, wildmicr, fertilizers, ready products or alternate IDs). Excluded from planning totals —
                  add a mapping to pull them in.
                </>
              )}
            </p>
          </div>

          {/* ── Removals & listing issues ─────────────────────────── */}
          <div className="air-columns">
            <div className="air-card">
              <h3>Removal orders <span className="air-chip">12 mo</span>{brandFiltered && <span className="air-chip muted">summary: all brands</span>}</h3>
              <div className="air-table-wrap">
                <table className="air-table">
                  <thead><tr>
                    <th>Type</th><th>Status</th>
                    <th className="air-num">Orders</th><th className="air-num">Requested</th>
                    <th className="air-num">Shipped</th><th className="air-num">Disposed</th><th className="air-num">In process</th>
                  </tr></thead>
                  <tbody>
                    {data.removals.summary.map((r, i) => (
                      <tr key={i}>
                        <td>{r.order_type}</td>
                        <td><Pill>{r.order_status}</Pill></td>
                        <td className="air-num">{fmt(r.orders)}</td>
                        <td className="air-num">{fmt(r.requested)}</td>
                        <td className="air-num">{fmt(r.shipped)}</td>
                        <td className="air-num">{fmt(r.disposed)}</td>
                        <td className={`air-num${num(r.in_process) > 0 ? ' neg' : ''}`}>{fmt(r.in_process)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {visiblePending.length > 0 && (
                <>
                  <h4 className="air-subhead">In process now <span className="air-chip warn">{removalInProcess} units</span></h4>
                  <div className="air-table-wrap">
                    <table className="air-table">
                      <thead><tr>
                        <th>Date</th><th>Product</th><th>Type</th><th>Disposition</th><th className="air-num">Units</th>
                      </tr></thead>
                      <tbody>
                        {visiblePending.map((r, i) => (
                          <tr key={i}>
                            <td className="air-mono">{r.request_date ? new Date(r.request_date).toLocaleDateString() : '—'}</td>
                            <td>
                              <div className="air-product">{(r.ASIN && data.names[r.ASIN]) || r.ASIN || '—'}</div>
                              <div className="air-asin">{r.sku}</div>
                            </td>
                            <td>{r.order_type}</td>
                            <td><Pill>{r.disposition === 'Unsellable' ? 'Unsellable' : r.disposition || '—'}</Pill></td>
                            <td className="air-num air-strong">{fmt(r.in_process_qty)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <p className="air-note">
                <b>Return</b> — inventory sent back to us · <b>Disposal</b> — destroyed ·
                <b> Liquidations</b> — sold off through Amazon's liquidation program ·
                <b> In process</b> — units currently being removed ·
                <b> Disposition</b> — Unsellable = damaged/expired stock, Sellable = sellable stock leaving (worth checking why).
              </p>
            </div>

            <div className="air-card">
              <h3>Listing issues <span className="air-chip">{visibleIssues.length}</span></h3>
              <div className="air-table-wrap air-issues">
                <table className="air-table">
                  <thead><tr>
                    <th>Status</th><th>SKU / ASIN &amp; issue</th><th className="air-num">FBA units</th>
                  </tr></thead>
                  <tbody>
                    {visibleIssues.map((r, i) => (
                      <tr key={i}>
                        <td><Pill>{r.status}</Pill></td>
                        <td>
                          <div className="air-mono">{r.sku} <span className="air-asin">· {r.asin}</span></div>
                          {(r.reason || r.issue) && (
                            <div className="air-issue-text">{r.reason ? <b>{r.reason}. </b> : ''}{r.issue}</div>
                          )}
                        </td>
                        <td className={`air-num${num(r.fba_units) > 0 ? ' neg' : ''}`}>
                          {num(r.fba_units) === 0 ? <span className="air-zero">0</span> : fmt(r.fba_units)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="air-note">
                Listings that can't sell right now: <b>Inactive</b> — listing switched off while stock may remain ·
                <b> Search Suppressed</b> — hidden from search until the issue is fixed.
                <b> FBA units</b> — stock stuck behind the problem.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AmazonInventoryReportPage;
